import Model, { attr, hasMany } from '@warp-drive/legacy/model';
import { Type } from '@warp-drive/core-types/symbols';
import { tracked } from '@glimmer/tracking';
import type { OutputData } from '@editorjs/editorjs';
import type CompanyLpnSetting from './company-lpn-setting.ts';
import type CompanySetting from './company-setting.ts';

export default class LpnPage extends Model {
  @attr declare badgeCappedTuition: boolean;
  @attr declare badgeDiscount: boolean;
  @attr declare badgeFamilySavings: boolean;
  @attr declare badgeFeesWaived: boolean;
  @attr declare description: OutputData;
  @attr declare discountPercent: string;
  @attr declare schoolName: string;
  @attr declare url: string;
  @attr declare dapip_id?: number;
  @attr declare isCompanyCreated?: boolean;
  @attr declare providedBy?: string;
  @attr declare badgeCappedTuitionRange?: string;
  @attr declare discountPercentRange?: string;
  @attr declare saveDollarAmountRange?: string;
  @attr declare saveDollarAmountValue?: string;
  @attr declare saveDollarAmount?: boolean;
  @attr declare partTuitionWaiver?: boolean;
  @attr declare lowTuition?: boolean;
  @attr declare costPerCredit?: string;
  @attr declare costPerCreditRange?: string;
  @attr declare format?: string[];
  @attr declare programs?: string[];
  @attr declare areasOfStudy?: string[];
  @attr declare imageUrl?: string;

  // Use Range toggle booleans
  @attr declare useDiscountPercentRange?: boolean;
  @attr declare useSaveDollarAmountRange?: boolean;
  @attr declare useCostPerCreditRange?: boolean;

  get savingsPercentRange(): string | undefined {
    return this.discountPercentRange;
  }

  set savingsPercentRange(value: string | undefined) {
    this.discountPercentRange = value;
  }

  get savingsAmountRange(): string | undefined {
    return this.saveDollarAmountRange;
  }

  set savingsAmountRange(value: string | undefined) {
    this.saveDollarAmountRange = value;
  }

  /*************************
   **  Relationships      **
   *************************/

  @hasMany('company-lpn-setting', { async: true, inverse: 'lpnPage' })
  declare companyLpnSettings: CompanyLpnSetting[];

  @tracked declare setting?: CompanyLpnSetting;
  @tracked declare isEnabled?: boolean;
  @tracked declare employerRecommended?: boolean;
  @tracked declare hasDiscount?: boolean;
  @tracked declare createdByOtherCompany?: boolean;

  /**
   * Find the CompanyLpnSetting for this page and the provided company setting.
   * Returns `undefined` if none exists.
   *
   * Usage:
   *   await lpnPage.settingForCompany(companySetting);      // or
   *   await lpnPage.settingForCompany(companySettingId);
   */
  async settingForCompany(
    companySettingOrId: CompanySetting | string | null | undefined
  ): Promise<CompanyLpnSetting | undefined> {
    if (!companySettingOrId) return undefined;

    const targetId =
      typeof companySettingOrId === 'string' ? companySettingOrId : companySettingOrId.id;

    // Resolve the async hasMany; already-included data will resolve instantly.
    const settings = await this.companyLpnSettings;

    return settings?.find((s) => s.companySetting?.id === targetId);
  }

  [Type] = 'lpn-page' as const;
}
